import {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  type FocusEvent,
} from "react";
import { useRecordQuotesStore } from "./quotesStore";
import QuoteEditor, { QuoteEditorData } from "./QuoteEditor";
import { Loader2, Check, AlertCircle } from "lucide-react";
import {
  QuoteResponse,
  QuoteStatusEnum,
  UpdateQuoteParamsSchema,
  QuoteLineTypeValues,
  HydratedQuoteLine,
} from "@forgepoint/shared";
import { useAutoSave } from "@/modules/quotesDeprecated/QuotePreview/hooks/useAutoSave";
import { cn } from "@/lib/utils";
import { isStaleWrite } from "@/lib/staleWrite";
import { formatDistance } from "date-fns";
import { usePriceListsStore } from "@/modules/priceLists/priceListsStore";
import { useQuoteSettingsStore } from "@/store/quoteSettingsStore";
import { useCanEditQuote } from "./useCanEditQuote";
import { getDateOnlyParam, mergeQuoteState } from "./mergeQuoteState";
import { hasUnsavedLines } from "./utils";
import { QUANTITY_INPUT_SELECTOR } from "./components/cells/QuantityCell";
import { useStandardAgentDisplayName } from "@/modules/standardAgent/useStandardAgentDisplayName";

interface QuotePreviewControllerProps {
  quoteId: string;
  showActionButtons?: boolean;
  refreshKey?: number;
  /** When true, the item editor renders inline over the quote instead of as a full-screen sheet. */
  inlineItemEditor?: boolean;
  /** Forwarded from QuoteEditor: fires when the inline item editor opens/closes. */
  onInlineItemEditorOpenChange?: (open: boolean) => void;
  /** Inline item editor only: toggles the surrounding chat panel from the item editor header. */
  onToggleChat?: () => void;
  /** Inline item editor only: whether the surrounding chat panel is collapsed. */
  isChatCollapsed?: boolean;
  /** True while the agent turn is running, so the quote may still change. */
  isAgentWorking?: boolean;
}

/** How long a save notice holds before the banner returns to the agent state. */
const AGENT_SAVE_NOTICE_MS = 5000;

// Extract editable data from a quote response
const quoteToEditorData = (quote: QuoteResponse): QuoteEditorData => ({
  name: quote.name || "",
  customerCompanyId: quote.customerCompanyId || null,
  priceListId: quote.priceListId || null,
  shippingAddressId: quote.shippingAddressId || null,
  locationId: quote.locationId ?? null,
  opportunityId: quote.opportunityId || null,
  salesRepUserId: quote.salesRepUserId ?? null,
  personIds: (quote.people ?? []).map((person) => person.id),
  deliveryTerms: quote.deliveryTerms || "",
  paymentTerms: quote.paymentTerms || "",
  termsOfService: quote.termsOfService || "",
  quoteFooter: quote.quoteFooter || "",
  expirationDate: quote.expirationDate ?? "",
  quoteDate: quote.quoteDate ?? "",
  showTotals: quote.showTotals ?? true,
  coverLetterMarkdown: quote.coverLetterMarkdown || "",
  customFields: quote.customFields ?? {},
  quoteCurrency: quote.quoteCurrency ?? null,
  snapshot: quote.snapshot ?? null,
  lines: (quote.lines || []).map((line) => {
    // Strip hydrated fields, keep only editable fields (preserve id)
    if (line.type === QuoteLineTypeValues.ITEM) {
      const itemLine = line;
      // Strip computed fields from quantityBreaks
      const editableBreaks = itemLine.quantityBreaks.map((qb) => ({
        quantity: qb.quantity,
        ...(qb.unitPriceOverride !== null && qb.unitPriceOverride !== undefined
          ? { unitPriceOverride: qb.unitPriceOverride }
          : {}),
      }));
      return {
        id: itemLine.id,
        type: itemLine.type,
        itemId: itemLine.itemId,
        description: itemLine.description,
        confidenceScore: itemLine.confidenceScore,
        reasoning: itemLine.reasoning,
        quantityBreaks: editableBreaks,
        customFields: itemLine.customFields,
        itemOverrides: itemLine.itemOverrides,
        unitOverride: itemLine.unitOverride,
        dimensionUnitOverride: itemLine.dimensionUnitOverride,
      };
    }
    // For section/note/discount lines, preserve id
    return {
      ...line,
      id: line.id,
    };
  }),
});

export function QuotePreviewController({
  quoteId,
  showActionButtons = true,
  refreshKey,
  inlineItemEditor = false,
  onInlineItemEditorOpenChange,
  onToggleChat,
  isChatCollapsed,
  isAgentWorking,
}: QuotePreviewControllerProps) {
  const { getQuote, updateQuote, quoteLoadingStates } = useRecordQuotesStore();
  const fetchCurrentPriceListForCompany = usePriceListsStore(
    (state) => state.fetchCurrentPriceListForCompany,
  );
  const quoteSettings = useQuoteSettingsStore((state) => state.settings);
  const fetchQuoteSettings = useQuoteSettingsStore(
    (state) => state.fetchSettings,
  );

  // Server quote (the source of truth for calculated fields)
  const [serverQuote, setServerQuote] = useState<QuoteResponse | null>(null);
  // Local editable data (controlled by this component)
  const [localData, setLocalData] = useState<QuoteEditorData | null>(null);
  // Hydrated lines from server (for calculated prices)
  const [hydratedLines, setHydratedLines] = useState<HydratedQuoteLine[]>([]);
  // The inline item editor renders over the quote; the autosave banner belongs
  // to the quote, so hide it while the item editor is open.
  const [inlineItemEditorOpen, setInlineItemEditorOpen] = useState(false);
  const handleInlineItemEditorOpenChange = useCallback(
    (nextOpen: boolean) => {
      setInlineItemEditorOpen(nextOpen);
      onInlineItemEditorOpenChange?.(nextOpen);
    },
    [onInlineItemEditorOpenChange],
  );

  // Mirrors for background refreshes: they must read the freshest values
  // without re-running effects per keystroke or per poll.
  const serverQuoteRef = useRef<QuoteResponse | null>(null);
  serverQuoteRef.current = serverQuote;
  const localDataRef = useRef<QuoteEditorData | null>(null);
  localDataRef.current = localData;
  // Real dirty tracking: the draft is unsaved while these differ. Edits bump
  // editSeq; a save marks savedSeq only up to the edits it actually carried,
  // so a keystroke landing mid-flight keeps the draft dirty. A failed or
  // rejected save never marks, so its edit survives background refreshes.
  const editSeqRef = useRef(0);
  const savedSeqRef = useRef(0);
  // The quote as the server last returned it, so saves can skip unchanged lines.
  const serverEditorDataRef = useRef<QuoteEditorData | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const showLocationField = quoteSettings?.showLocationField ?? false;

  useEffect(() => {
    if (!quoteSettings) {
      fetchQuoteSettings();
    }
  }, [quoteSettings, fetchQuoteSettings]);

  // Save handler for auto-save
  const onSave = useCallback(
    async (data: QuoteEditorData) => {
      if (!serverQuote?.id) {
        return;
      }
      const seqAtSave = editSeqRef.current;
      const sendsLines = hasUnsavedLines(data, serverEditorDataRef.current);

      const updateData = {
        name: data.name || undefined,
        customerCompanyId: data.customerCompanyId || undefined,
        priceListId: data.priceListId,
        shippingAddressId: data.shippingAddressId,
        ...(showLocationField ? { locationId: data.locationId } : {}),
        opportunityId:
          data.opportunityId === "" ? undefined : data.opportunityId,
        salesRepUserId: data.salesRepUserId,
        personIds: data.personIds,
        deliveryTerms: data.deliveryTerms,
        paymentTerms: data.paymentTerms,
        termsOfService: data.termsOfService,
        quoteFooter: data.quoteFooter,
        expirationDate: getDateOnlyParam(data.expirationDate),
        quoteDate: getDateOnlyParam(data.quoteDate),
        ...(sendsLines ? { lines: data.lines } : {}),
        showTotals: data.showTotals,
        customFields: data.customFields,
        quoteCurrency: data.quoteCurrency,
      };

      const result = await updateQuote(serverQuote.id, updateData);
      if (result.error) {
        setValidationError(result.error);
        return;
      }

      if (!result.quote) {
        setValidationError("Failed to update quote");
        return;
      }
      const savedEditorData = quoteToEditorData(result.quote);
      serverEditorDataRef.current = savedEditorData;
      // An unsent draft's lines may be stale against changes made elsewhere,
      // so the server's lines replace them unless edited since this save.
      const savedLines = sendsLines ? data.lines : savedEditorData.lines;

      // Always accept server-resolved salesRepUserId — the backend is
      // authoritative for this field (e.g. re-resolved when customer changes)
      const resolvedSalesRepUserId = result.quote?.salesRepUserId ?? null;
      setLocalData((prev) => {
        if (!prev) {
          return prev;
        }
        const lines = prev.lines === data.lines ? savedLines : prev.lines;
        return { ...prev, lines, salesRepUserId: resolvedSalesRepUserId };
      });

      // Merge server-calculated fields (prices, totals) with the saved data.
      // We intentionally do NOT overwrite localData — the user may have
      // continued editing while the save was in-flight.
      const { quote: mergedQuote, hydratedLines: newHydratedLines } =
        mergeQuoteState(result.quote, { ...data, lines: savedLines });
      setServerQuote(mergedQuote);
      setHydratedLines(newHydratedLines);
      savedSeqRef.current = seqAtSave;
      setLastSaved(new Date());
      setValidationError(null);
    },
    [serverQuote?.id, showLocationField, updateQuote],
  );

  const { autoSaving, queueEdit, deferEdit, resumeDeferredEdit } = useAutoSave({
    onSave,
    timeout: 1000,
  });
  const autoSavingRef = useRef(false);
  autoSavingRef.current = autoSaving;

  /**
   * Applies a freshly fetched quote without closing the open view. When the
   * user hasn't edited recently, the draft re-seeds from the server; while
   * they are mid-edit, the draft is kept and only server-calculated fields
   * merge in (last write wins on the next autosave).
   */
  const applyFreshQuote = useCallback((fresh: QuoteResponse) => {
    // A different quote is an initial seed, never a merge or a stale-skip:
    // identity checks before version checks (mounts are keyed by quoteId, so
    // this is belt-and-braces for any unkeyed caller).
    const isSameQuote = serverQuoteRef.current?.id === fresh.id;
    if (isSameQuote && isStaleWrite(serverQuoteRef.current, fresh)) {
      return;
    }
    serverEditorDataRef.current = quoteToEditorData(fresh);
    const prevLocal = localDataRef.current;
    // A finalized quote is immutable, so it lands whole even over a dirty
    // draft; archived drafts stay autosave-editable.
    const keepDraft =
      isSameQuote &&
      fresh.status !== QuoteStatusEnum.FINALIZED &&
      (autoSavingRef.current || editSeqRef.current !== savedSeqRef.current);
    if (!prevLocal || !keepDraft) {
      savedSeqRef.current = editSeqRef.current;
      setServerQuote(fresh);
      setLocalData(quoteToEditorData(fresh));
      setHydratedLines(fresh.lines || []);
      return;
    }
    const { quote: mergedQuote, hydratedLines: mergedLines } = mergeQuoteState(
      fresh,
      prevLocal,
    );
    setServerQuote(mergedQuote);
    setHydratedLines(mergedLines);
  }, []);

  // Load quote on mount; refreshKey re-runs are background refreshes that
  // must not unmount the editor (no loading state, errors ignored).
  useEffect(() => {
    const loadQuote = async () => {
      const isInitialLoad = serverQuoteRef.current === null;
      if (isInitialLoad) {
        setLoading(true);
        setError(null);
      }

      try {
        const fetchedQuote = await getQuote(quoteId);
        if (fetchedQuote) {
          applyFreshQuote(fetchedQuote);
        } else if (isInitialLoad) {
          setError("Quote not found");
        }
      } catch (_err) {
        if (isInitialLoad) {
          setError("Failed to load quote");
        }
      } finally {
        if (isInitialLoad) {
          setLoading(false);
        }
      }
    };

    loadQuote();
  }, [quoteId, getQuote, refreshKey, applyFreshQuote]);

  // Live refresh: when the store copy of this quote updates (e.g. the agent
  // edited it mid-thread), merge it into the open view instead of requiring
  // a close/reopen.
  const storeQuote = useRecordQuotesStore((state) =>
    serverQuote?.id ? state.quotes.get(serverQuote.id) : undefined,
  );
  useEffect(() => {
    if (!storeQuote || !serverQuote) {
      return;
    }
    // Equal-time document edits must arrive with their hydrated render fields.
    if (
      isStaleWrite(serverQuote, storeQuote) ||
      (storeQuote.updatedAt.getTime() === serverQuote.updatedAt.getTime() &&
        (storeQuote.quoteDocument === undefined ||
          storeQuote.assetUrls === undefined ||
          storeQuote.quoteDocument === serverQuote.quoteDocument))
    ) {
      return;
    }
    applyFreshQuote(storeQuote);
  }, [storeQuote, serverQuote, applyFreshQuote]);

  // Handle data changes from the editor
  const handleDataChange = useCallback(
    async (newData: QuoteEditorData) => {
      if (!serverQuote || serverQuote.status === QuoteStatusEnum.FINALIZED) {
        return;
      }
      const nextData =
        newData.customerCompanyId === localData?.customerCompanyId
          ? newData
          : await resolvePreviewPriceListData(
              newData,
              fetchCurrentPriceListForCompany,
            );

      // Bumped with the edit's application: bumping before the await would
      // let a save that fired mid-await mark this edit saved unsent.
      editSeqRef.current += 1;
      setLocalData(nextData);

      // Validate and queue for auto-save
      const updateParams = {
        name: nextData.name || undefined,
        customerCompanyId: nextData.customerCompanyId || undefined,
        priceListId: nextData.priceListId,
        shippingAddressId: nextData.shippingAddressId,
        ...(showLocationField ? { locationId: nextData.locationId } : {}),
        deliveryTerms: nextData.deliveryTerms,
        paymentTerms: nextData.paymentTerms,
        termsOfService: nextData.termsOfService,
        quoteFooter: nextData.quoteFooter,
        expirationDate: getDateOnlyParam(nextData.expirationDate),
        quoteDate: getDateOnlyParam(nextData.quoteDate),
        lines: nextData.lines,
        quoteCurrency: nextData.quoteCurrency,
      };

      const result = UpdateQuoteParamsSchema.safeParse(updateParams);
      if (result.success) {
        setValidationError(null);
        // A half-typed quantity must not save, so it waits for the blur.
        if (document.activeElement?.matches(QUANTITY_INPUT_SELECTOR)) {
          deferEdit(nextData);
        } else {
          queueEdit(nextData);
        }
      } else {
        setValidationError(
          result.error.issues[0]?.message || "Invalid quote data",
        );
      }
    },
    [
      fetchCurrentPriceListForCompany,
      localData?.customerCompanyId,
      queueEdit,
      deferEdit,
      serverQuote,
      showLocationField,
    ],
  );

  /**
   * Starts the autosave debounce for a quantity edit deferred while its input
   * was focused. Saving on the blur itself would drop focus mid-Tab.
   * @param event - The focus event bubbled from inside the editor
   */
  const handleEditorBlur = useCallback(
    (event: FocusEvent<HTMLDivElement>) => {
      if (event.target.matches(QUANTITY_INPUT_SELECTOR)) {
        resumeDeferredEdit();
      }
    },
    [resumeDeferredEdit],
  );

  // Keep the hydrated quote document coherent with the editor after autosave,
  // so switching back to the quote/preview view renders the freshly saved HTML.
  const handleDocumentSaved = useCallback((html: string) => {
    setServerQuote((prev) => (prev ? { ...prev, quoteDocument: html } : prev));
  }, []);

  // Handle refresh (e.g., after BOM update)
  const handleRefresh = useCallback(async () => {
    if (!serverQuote?.id) {
      return;
    }

    try {
      const fetchedQuote = await getQuote(serverQuote.id);
      if (fetchedQuote) {
        applyFreshQuote(fetchedQuote);
      }
    } catch (_err) {
      // Silently fail refresh
    }
  }, [serverQuote?.id, getQuote, applyFreshQuote]);

  // Computed quote for display (merge server + local)
  const displayQuote = useMemo(() => {
    if (!serverQuote || !localData) {
      return serverQuote;
    }
    return {
      ...serverQuote,
      name: localData.name || serverQuote.name,
      customerCompanyId:
        localData.customerCompanyId || serverQuote.customerCompanyId,
      shippingAddressId:
        localData.shippingAddressId ?? serverQuote.shippingAddressId,
      locationId: localData.locationId ?? serverQuote.locationId,
      deliveryTerms: localData.deliveryTerms || serverQuote.deliveryTerms,
      paymentTerms: localData.paymentTerms || serverQuote.paymentTerms,
      termsOfService: localData.termsOfService || serverQuote.termsOfService,
      quoteFooter: localData.quoteFooter || serverQuote.quoteFooter,
      expirationDate: getDateOnlyParam(localData.expirationDate),
      quoteDate: getDateOnlyParam(localData.quoteDate),
      quoteCurrency: localData.quoteCurrency ?? serverQuote.quoteCurrency,
      snapshot: localData.snapshot ?? serverQuote.snapshot,
    };
  }, [serverQuote, localData]);

  const canManagePublicAccess = useCanEditQuote(serverQuote);

  const agentName = useStandardAgentDisplayName();

  // While the agent works the banner rests on its own state. A save takes it
  // over briefly so the user still sees their own edit land, then it returns.
  const [saveNoticeVisible, setSaveNoticeVisible] = useState(false);
  useEffect(() => {
    if (!lastSaved) {
      return;
    }
    setSaveNoticeVisible(true);
    const timer = setTimeout(
      () => setSaveNoticeVisible(false),
      AGENT_SAVE_NOTICE_MS,
    );
    return () => clearTimeout(timer);
  }, [lastSaved]);

  const loadingState = serverQuote?.id
    ? quoteLoadingStates.get(serverQuote.id)
    : null;
  const isLoading = loading || loadingState?.isLoadingGet;

  if (isLoading && !serverQuote) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !localData) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-red-500">{error || "Failed to load quote"}</p>
      </div>
    );
  }

  const isFinalized = serverQuote?.status === QuoteStatusEnum.FINALIZED;
  const canEdit = showActionButtons && !isFinalized;
  const canEditOpportunity =
    canManagePublicAccess && showActionButtons && !serverQuote?.archivedAt;

  const showAgentWorking =
    Boolean(isAgentWorking) &&
    !validationError &&
    !autoSaving &&
    !saveNoticeVisible;

  const getStatusMessage = () => {
    if (validationError) {
      return validationError;
    }
    if (autoSaving) {
      return "Saving...";
    }
    if (lastSaved) {
      const now = new Date();
      const diffSeconds = Math.floor(
        (now.getTime() - lastSaved.getTime()) / 1000,
      );
      if (diffSeconds < 60) {
        return "Saved";
      }
      return `Saved ${diffSeconds < 120 ? "1 minute ago" : `${Math.floor(diffSeconds / 60)} minutes ago`}`;
    }
    if (serverQuote) {
      return `Last updated ${formatDistance(serverQuote.updatedAt, new Date(), { addSuffix: true })}`;
    }
    return null;
  };

  const statusMessage = getStatusMessage();

  return (
    <div className="flex flex-col h-full">
      {canEdit && !inlineItemEditorOpen && (
        <div
          className={cn(
            "flex items-center gap-2 px-4 py-2 text-sm border-b",
            validationError
              ? "bg-red-50 text-red-700 border-red-200"
              : showAgentWorking
                ? "bg-amber-50 text-amber-700 border-amber-200 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-400"
                : autoSaving
                  ? "bg-blue-50 text-blue-700 border-blue-200"
                  : "bg-green-50 text-green-700 border-green-200",
          )}
        >
          {validationError ? (
            <AlertCircle className="h-4 w-4" />
          ) : showAgentWorking || autoSaving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Check className="h-4 w-4" />
          )}
          <span>
            {showAgentWorking
              ? `${agentName} is working. Conflicting changes may be overwritten.`
              : statusMessage}
          </span>
        </div>
      )}
      <div className="flex-1 overflow-hidden" onBlur={handleEditorBlur}>
        <QuoteEditor
          quote={displayQuote}
          data={localData}
          hydratedLines={hydratedLines}
          isDirty={false} // since we have auto-save enabled, the dirty state just causes jumpiness
          readOnly={!canEdit}
          canManagePublicAccess={canManagePublicAccess}
          canEditOpportunity={canEditOpportunity}
          showHeader={true}
          showActionButtons={false}
          showLocationField={showLocationField}
          enableCoverLetter={
            serverQuote?.enableCoverLetter ??
            quoteSettings?.enableCoverLetter ??
            false
          }
          customFieldsSchema={quoteSettings?.customFieldsSchema}
          inlineItemEditor={inlineItemEditor}
          onInlineItemEditorOpenChange={handleInlineItemEditorOpenChange}
          onToggleChat={onToggleChat}
          isChatCollapsed={isChatCollapsed}
          isAgentWorking={isAgentWorking}
          onDataChange={handleDataChange}
          onRefresh={handleRefresh}
          onDocumentSaved={handleDocumentSaved}
        />
      </div>
    </div>
  );
}

async function resolvePreviewPriceListData(
  data: QuoteEditorData,
  fetchCurrentPriceListForCompany: (
    companyId: string,
  ) => Promise<string | null>,
): Promise<QuoteEditorData> {
  if (!data.customerCompanyId) {
    return { ...data, priceListId: null };
  }

  return {
    ...data,
    priceListId: await fetchCurrentPriceListForCompany(data.customerCompanyId),
  };
}
